import { motion } from 'framer-motion';
import {
  TrendingUp,
  Rocket,
  Globe,
  ArrowRight,
  Lightbulb,
  Zap,
  AlertCircle,
  CheckCircle,
  Shield,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function GrowthStrategyPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const ansoffMatrix = [
    {
      icon: TrendingUp,
      title: 'Market Penetration',
      subtitle: 'Existing Products → Existing Markets',
      description:
        "Increase share in current markets with current products. Lowest risk growth path because you're operating in familiar territory.",
      tactics: [
        'Increase marketing and sales efforts',
        'Acquire or merge with competitors',
        'Incentivize existing customers to buy more',
        "Win competitors' customers",
        'Improve distribution and availability',
      ],
      example: {
        company: 'Coca-Cola',
        strategy:
          'Continuously increases share in mature markets through omnipresent distribution, marketing campaigns, and pricing strategies',
      },
      risk: 'Low',
      when: 'Market is growing, you have capacity, and share gains are achievable. Best first step for growth.',
    },
    {
      icon: Rocket,
      title: 'Product Development',
      subtitle: 'New Products → Existing Markets',
      description:
        'Create new products for your existing customers. Moderate risk because you know the market, but product innovation is uncertain.',
      tactics: [
        'Launch new product lines or variations',
        'Add features to existing products',
        'Develop complementary products',
        'Innovate on form factor or delivery',
        'Acquire technology or companies for new products',
      ],
      example: {
        company: 'Apple',
        strategy:
          'iPhone → iPad → Apple Watch → AirPods. Each new product sold to existing Apple customer base with existing brand and channels.',
      },
      risk: 'Medium',
      when: 'Customers want more from you, you have innovation capability, and market can absorb new products. Strong brand helps.',
    },
    {
      icon: Globe,
      title: 'Market Development',
      subtitle: 'Existing Products → New Markets',
      description:
        'Take current products to new geographic markets, customer segments, or channels. Moderate risk because product is proven but market is new.',
      tactics: [
        'Expand to new geographies',
        'Target new customer segments',
        'Enter new distribution channels',
        'Adapt products for new use cases',
        'Partner with local players',
      ],
      example: {
        company: 'Netflix',
        strategy:
          'Proven streaming model expanded from US to 190+ countries. Same product, different markets, localized content.',
      },
      risk: 'Medium',
      when: 'Product has proven market fit, new markets have similar needs, and you can adapt to local conditions. Requires market research.',
    },
    {
      icon: Zap,
      title: 'Diversification',
      subtitle: 'New Products → New Markets',
      description:
        'Enter entirely new businesses—new products in new markets. Highest risk because nothing is familiar. Rarely succeeds unless leveraging core competency.',
      tactics: [
        'Acquire companies in new industries',
        'Build new business units',
        'Partner for new ventures',
        'License IP for new applications',
        'Leverage competency in new context',
      ],
      example: {
        company: 'Amazon',
        strategy:
          'Retail → AWS (cloud computing). Leveraged infrastructure competency for completely new market. High risk but massive success.',
      },
      risk: 'High',
      when: 'Only when core business is mature/declining, or when new opportunity leverages unique competency. Most diversification fails.',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Strategy Formulation', path: '/strategy-formulation' },
              { label: 'Growth Strategy' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Lightbulb className="w-4 h-4 mr-2" />
            Strategy Formulation Tool 2 of 3
          </span>
        }
        title="Choose a growth path your business can support"
        subtitle="Explore where growth could come from before spreading your resources too thin. Compare new markets, new products, and deeper customer relationships with the Ansoff matrix."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Plan your growth
        </Link>
      </PageHero>

      {/* Ansoff Matrix */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Compare growth options before committing</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The Ansoff matrix makes the choice explicit: more of what works, new customers, new
              products, or a larger change. Assess each path against your capabilities and appetite
              for risk.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {ansoffMatrix.map((strategy, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-600 p-3 rounded-lg">
                      <strategy.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{strategy.title}</h3>
                      <p className="text-sm text-blue-700 font-medium">{strategy.subtitle}</p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold ${strategy.risk === 'Low' ? 'bg-green-100 text-green-700' : strategy.risk === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}
                  >
                    {strategy.risk} Risk
                  </span>
                </div>
                <p className="text-gray-700 mb-4">{strategy.description}</p>
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Growth Tactics:</p>
                  <ul className="space-y-1">
                    {strategy.tactics.map((t, j) => (
                      <li key={j} className="text-xs text-gray-700 flex items-start">
                        <CheckCircle className="w-3 h-3 text-green-600 mr-1.5 mt-0.5 flex-shrink-0" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-green-50 p-3 rounded-lg mb-3">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-green-700">Example:</span>{' '}
                    {strategy.example.company} — {strategy.example.strategy}
                  </p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-purple-700">Best When:</span>{' '}
                    {strategy.when}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Choose Your Growth Path</h2>
              <p className="text-xl text-gray-600">
                A systematic framework for selecting the right growth strategy
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Start with Lowest Risk Path',
                  description:
                    "Unless forced otherwise, start with market penetration (grow share in current market). It's lowest risk and builds on existing strengths. Only move to higher-risk paths when penetration maxes out.",
                  tip: 'Ask: "Can we grow 20%+ annually through market penetration alone?" If yes, do that first. Higher-risk paths should be secondary, not primary.',
                },
                {
                  step: '2',
                  title: 'Assess Market Maturity',
                  description:
                    'In growing markets, penetration works. In mature/declining markets, you must look elsewhere—product development, new markets, or diversification become necessary.',
                  tip: 'Calculate market growth rate. If <3% annually, market is mature. If shrinking, penetration is hard—look to new markets or products.',
                },
                {
                  step: '3',
                  title: 'Evaluate Your Capabilities',
                  description:
                    'Product development requires innovation. Market development requires go-to-market capability. Diversification requires transferable competencies. Choose paths where you have (or can build) necessary capabilities.',
                  tip: 'Score each growth option: Do we have capabilities (1-5)? Can we acquire them (1-5)? Pick highest scores.',
                },
                {
                  step: '4',
                  title: 'Calculate Resource Requirements',
                  description:
                    'Each growth path has different resource demands. Penetration = sales/marketing. Product development = R&D. Market expansion = distribution. Diversification = everything. Can you afford chosen path?',
                  tip: 'Build 3-year financial model for each growth option. Many attractive growth paths fail due to insufficient capital or time to breakeven.',
                },
                {
                  step: '5',
                  title: 'Consider Portfolio Approach',
                  description:
                    "You don't have to pick just one. Allocate: 70% penetration (core growth), 20% product development (adjacency), 10% new markets (options). Diversify risk while maintaining focus.",
                  tip: 'Use "core, adjacent, transformational" framework: 70% core (penetration), 20% adjacent (product/market dev), 10% transformational (diversification).',
                },
                {
                  step: '6',
                  title: 'Set Clear Metrics and Milestones',
                  description:
                    'For each growth path, define: How will we measure success? What are key milestones? When do we double down vs. pivot? Clear metrics enable learning and adjustment.',
                  tip: 'Define failure criteria upfront: "If we don\'t achieve X by Y, we\'ll stop/pivot." Prevents escalating commitment to failing paths.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Growth Strategy Mistakes</h2>
              <p className="text-xl text-gray-600">Avoid these traps that derail growth</p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: "Pursuing Growth for Growth's Sake",
                  description:
                    "Growing revenue while destroying profitability. Or growing into unprofitable markets/products. Growth that weakens the company is not strategic—it's destructive.",
                  fix: 'Set profitability guardrails. Growth must improve unit economics over time, not worsen them. "Grow to scale" is valid; "grow and figure out profit later" rarely works.',
                },
                {
                  mistake: 'Too Many Growth Initiatives',
                  description:
                    'Pursuing market penetration AND product development AND new markets AND diversification simultaneously. Resources spread thin, nothing succeeds.',
                  fix: 'Pick 1-2 primary growth paths. Focus beats diversification. You can explore others at 10-20% resource level, but 70%+ should be concentrated.',
                },
                {
                  mistake: 'Ignoring Market Attractiveness',
                  description:
                    'Growing into unattractive markets with low margins, high competition, or structural challenges. Revenue growth in bad markets destroys value.',
                  fix: "Use Porter's Five Forces on each growth opportunity. Only grow into attractive markets where you can defend position and earn returns.",
                },
                {
                  mistake: 'Overestimating Transferability',
                  description:
                    'Assuming success in Market A guarantees success in Market B. Or that competency in Product X transfers to Product Y. Most capabilities are more context-specific than we think.',
                  fix: 'Test assumptions with pilots and experiments before full commitment. Many "adjacent" markets turn out to be completely different.',
                },
                {
                  mistake: 'Neglecting the Core',
                  description:
                    'Chasing shiny new opportunities while existing business deteriorates. Growth initiatives distract from defending and growing core business—which still generates most revenue/profit.',
                  fix: 'Use 70-20-10 rule: 70% resources on core business, 20% adjacent, 10% transformational. Never starve the core to fund experiments.',
                },
                {
                  mistake: 'No Clear Success Criteria',
                  description:
                    "Launching growth initiatives without defining success. How much market share? By when? At what cost? Without metrics, can't tell if it's working until too late.",
                  fix: 'For each growth path: Define target metrics (revenue, share, customers), timeline (6mo/12mo/24mo milestones), and investment limit. Track ruthlessly.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Turn growth ambition into a considered direction
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Explore the options</h3>
                <p className="text-gray-600">
                  Compare market penetration, market development, product development, and
                  diversification.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Test the assumptions</h3>
                <p className="text-gray-600">
                  Ask what customers need, what your company can deliver, and what each path would
                  require.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Set the priorities</h3>
                <p className="text-gray-600">
                  Choose a direction and connect it to the investments and initiatives needed to
                  pursue it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/external-assessment/market-analysis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Globe className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">Market Analysis</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Assess market size, growth, and attractiveness for expansion
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/competitive-strategy"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Shield className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Competitive Strategy</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Ensure growth aligns with your competitive approach
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/strategic-initiatives"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Zap className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Strategic Initiatives</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Turn growth strategy into specific initiatives
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/strategy-formulation/competitive-strategy"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Competitive Strategy
              </Link>
              <Link
                to="/strategy-formulation/strategic-initiatives"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Strategic Initiatives <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">Plan your growth in StratPlan</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Choose a direction and connect it to the investments and initiatives needed to pursue
            it. Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Plan your growth <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
